// src/pages/ChatOnlyPage.tsx
// Author: CorestratAI Solutions
// Centered chat interface for general conversations with smart routing to workspace

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { chatApi } from '../services/api';
import { Message, Attachment } from '../types/chat';
import { getRandomTagline } from '../utils/taglines';
import { trackChatMessageSent, trackProBannerDismissed, trackProBannerClicked } from '../utils/analytics';
import ThemeToggle from '../components/ThemeToggle';
import CodeDisplay from '../components/CodeDisplay';
import CodebookDualCodeBlock from '../components/Codebook/CodebookDualCodeBlock';
import DashboardPreview from '../components/DashboardPreview';
import CreditBalance from '../components/CreditBalance';
import Sidebar from '../components/Sidebar';
import ThinkingSteps from '../components/ThinkingSteps';
import StatusNotification from '../components/StatusNotification';
import ExecutionStages from '../components/ExecutionStages';
import WorkflowMessage from '../components/WorkflowMessage';
import DocumentationButton from '../components/Documentation/DocumentationButton';
import DocumentationModal from '../components/Documentation/DocumentationModal';
import OnboardingModal, { OnboardingMode } from '../components/OnboardingModal';
import { onboardingApi } from '../services/api';
import ProTrialBanner from '../components/ProTrialBanner';
import { useChatStore } from '../stores/chatStore';
import { detectBookmateTrigger, detectWorkflowTrigger, isYesResponse, extractYesModifier, buildPrefillPrompt } from '../utils/concierge';

const ChatOnlyPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { logout, isGuest, user } = useAuth();

  // Handler to convert guest to authenticated user
  const handleGuestSignIn = () => {
    console.log('[CHAT] Guest user clicking Sign In - clearing guest session');
    // Clear guest session to allow login
    localStorage.removeItem('guest_session_id');
    localStorage.removeItem('guest_executions_used');
    localStorage.removeItem('guest_messages_used');
    // Navigate to login
    navigate('/login');
  };

  // Zustand store — state survives tab switches
  const {
    messages, setMessages,
    input, setInput,
    loading, setLoading,
    currentSessionId, setCurrentSessionId,
    selectedFiles, setSelectedFiles,
    statusMessage, setStatusMessage,
    statusProgress, setStatusProgress,
    workspaceModalOpen, setWorkspaceModalOpen,
    workspaceContent, setWorkspaceContent,
    thinkingSteps, setThinkingSteps,
    executionStages, setExecutionStages,
    isStreamingActive, setIsStreamingActive,
    pendingSuggestion, setPendingSuggestion,
    pathContext, setPathContext,
  } = useChatStore();

  // Local state — ephemeral, doesn't need to survive tab switch
  const [convertingToPdf, setConvertingToPdf] = useState<string | null>(null);
  const [copiedBlockId, setCopiedBlockId] = useState<string | null>(null);
  const [tagline] = useState<string>(getRandomTagline());
  const [guestUsage, setGuestUsage] = useState({ executions_remaining: 5, messages_remaining: 10 });
  const [hasToken, setHasToken] = useState(false);
  const [documentationOpen, setDocumentationOpen] = useState(false);
  // Onboarding/welcome modal (docs/onboarding/v1-spec.md). Guests keep the old
  // WelcomeModal behavior (overview step only, login_count heuristic); authed
  // users are server-driven — onboarding_status === 'pending' opens the full
  // questionnaire flow (decided by the status fetch below).
  const [welcomeMode, setWelcomeMode] = useState<OnboardingMode | null>(() => {
    if (sessionStorage.getItem('welcome_dismissed')) return null;
    const authed = !!(localStorage.getItem('access_token') || sessionStorage.getItem('auth_token'));
    if (authed) return null;
    const loginCount = parseInt(sessionStorage.getItem('login_count') || '0', 10);
    return loginCount <= 1 ? 'welcome' : null;
  });
  const [showProTrial, setShowProTrial] = useState(false);
  // One-shot: a path deep link auto-sends its lesson prompt once the session
  // is ready. Clicking Start → on the node is the consent; the prompt is
  // machine-facing (execution guards) and must not sit in an editable box.
  const [pendingAutoSend, setPendingAutoSend] = useState(false);
  // Long user messages collapse to 3 lines with an expand chevron (Google AI
  // pattern, Francis 2026-09-02) — path prompts and pasted walls of text
  // shouldn't push the answer below the fold.
  const [expandedUserMsgs, setExpandedUserMsgs] = useState<Set<string>>(new Set());
  const isLongUserMsg = (content: string) =>
    content.length > 280 || content.split('\n').length > 4;
  const toggleUserMsg = (id: string) =>
    setExpandedUserMsgs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Authed users: ask the server whether onboarding is still pending. Existing
  // users are backfilled to 'skipped' (migration 028) so only fresh signups
  // ever get the questionnaire here.
  useEffect(() => {
    if (!hasToken || sessionStorage.getItem('welcome_dismissed')) return;
    onboardingApi.getStatus()
      .then((s) => {
        if (s.onboarding_status === 'pending') setWelcomeMode('full');
      })
      .catch(() => { /* fail open — never block chat on this */ });
  }, [hasToken]);

  // Show Pro Trial banner only after user data is loaded and confirmed basic
  useEffect(() => {
    if (user) {
      const tier = (user as any)?.subscription_tier || 'basic';
      const dismissed = sessionStorage.getItem('pro_trial_dismissed');
      setShowProTrial(tier === 'basic' && !dismissed);
    }
  }, [user]);
  // Acquisition attribution captured from the deep-link URL (blog "Run in
  // Cubemate" buttons). Ref, not state: it never drives rendering and must
  // survive until the first send without re-render races.
  const deepLinkSourceRef = useRef<{ source: string; ref?: string } | null>(null);

  // Pre-fill the input from ?prompt= (same mechanism as /bookmate and
  // /workflow). Path deep links (&path=&node= present) additionally AUTO-SEND
  // once the session is ready: clicking Start → on the node was the consent,
  // and the machine-facing prompt must not sit in an editable box. Plain
  // ?prompt= (concierge, Help) stays review-then-send.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promptParam = params.get('prompt');
    const pathParam = params.get('path');
    const nodeParam = params.get('node');
    // Acquisition attribution (e.g. blog "Run in Cubemate" buttons send
    // ?source=blog&ref=<post slug>). One-shot: attached to the first send
    // only, then cleared — see handleSendMessage.
    const sourceParam = params.get('source');
    if (sourceParam) {
      deepLinkSourceRef.current = { source: sourceParam, ref: params.get('ref') || undefined };
    }
    if (promptParam) {
      setInput(promptParam);
      const fromPath = !!(pathParam && nodeParam);
      setPathContext(fromPath ? { pathId: pathParam!, nodeId: nodeParam! } : null);
      // A path step must not inherit a concierge suggestion left over from an
      // earlier chat — a stray "yes" would dispatch to Bookmate/Workflow.
      if (fromPath) {
        setPendingSuggestion(null);
        setPendingAutoSend(true);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire the queued path-step prompt as soon as a session exists and nothing
  // is in flight. One-shot: flag drops before the send so it can never
  // double-fire; if the send path bails, the prompt stays in the input and
  // the student sends manually (the old behavior).
  useEffect(() => {
    if (pendingAutoSend && currentSessionId && !loading && input.trim()) {
      setPendingAutoSend(false);
      handleSendMessage({ preventDefault: () => {} } as React.FormEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoSend, currentSessionId, loading, input]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);

  // Auto-scroll: when streaming completes, scroll to bottom to show full response
  useEffect(() => {
    if (!isStreamingActive && !loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreamingActive, loading]);

  // ChatGPT-style: when user sends a message, scroll so their message is near the top
  // This gives maximum visible space for the AI response streaming in below
  useEffect(() => {
    if (isStreamingActive && lastUserMessageRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        const userMsgEl = lastUserMessageRef.current;
        const containerRect = container.getBoundingClientRect();
        const msgRect = userMsgEl.getBoundingClientRect();
        const offsetFromTop = msgRect.top - containerRect.top + container.scrollTop;
        // Position user message ~60px from top of scroll area
        container.scrollTo({ top: offsetFromTop - 60, behavior: 'smooth' });
      }
    }
  }, [isStreamingActive]);

  // Monitor token availability with polling (like CreditBalance does)
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('auth_token');
      const tokenAvailable = !!token;

      if (tokenAvailable && !hasToken) {
        console.log('[CHAT] Token became available');
        setHasToken(true);
      } else if (!tokenAvailable && hasToken) {
        console.log('[CHAT] Token removed');
        setHasToken(false);
      }
    };

    // Check immediately
    checkToken();

    // Poll every 500ms for the first 5 seconds after mount (catches login token)
    const tokenCheckInterval = setInterval(checkToken, 500);

    // Stop polling after 5 seconds
    const stopCheckTimeout = setTimeout(() => {
      clearInterval(tokenCheckInterval);
    }, 5000);

    return () => {
      clearInterval(tokenCheckInterval);
      clearTimeout(stopCheckTimeout);
    };
  }, []); // Empty deps - only run on mount

  // Listen for guest usage updates from API responses
  useEffect(() => {
    const handleGuestUsageUpdate = (event: any) => {
      const guestInfo = event.detail;
      console.log('[CHAT] Guest usage update received:', guestInfo);
      setGuestUsage({
        executions_remaining: guestInfo.executions_remaining || 5,
        messages_remaining: guestInfo.messages_remaining || 10
      });
    };

    window.addEventListener('guest-usage-update', handleGuestUsageUpdate);
    return () => window.removeEventListener('guest-usage-update', handleGuestUsageUpdate);
  }, []);

  // URL-based session initialization
  useEffect(() => {
    const initSession = async () => {
      if (urlSessionId) {
        // URL has session ID - save it and load from backend (Redis cache)
        localStorage.setItem('lastCodemateSession', urlSessionId);
        setCurrentSessionId(urlSessionId);
        if (messages.length === 0) {
          try {
            const data = await chatApi.getSessionMessages(urlSessionId);
            const loaded = data.messages.map((msg: any) => ({
              id: msg.id || msg.message_id || Date.now().toString(),
              type: msg.role === 'user' ? 'user' : 'system',
              content: msg.content,
              timestamp: new Date(msg.timestamp),
            }));
            setMessages(loaded);
          } catch (error) {
            console.error('[CHAT] Failed to load session messages:', error);
            localStorage.removeItem('lastCodemateSession');
            navigate('/codemate', { replace: true });
          }
        }
        return;
      }
      // No session ID in URL - check if there's a recent session to restore
      if (!isGuest) {
        const lastSession = localStorage.getItem('lastCodemateSession');
        if (lastSession) {
          console.log('[CHAT] Restoring last session:', lastSession);
          navigate(`/codemate/${lastSession}`, { replace: true });
          return;
        }
        // No previous session - create new one
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('auth_token');
        if (!token) return;
        try {
          const session = await chatApi.startSession();
          navigate(`/codemate/${session.session_id}`, { replace: true });
        } catch (error) {
          console.error('[CHAT] Failed to create session:', error);
        }
      } else {
        const guestId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
        setCurrentSessionId(guestId);
        setMessages([]);
      }
    };
    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSessionId]);

  // Token-availability fallback: create session once token arrives
  useEffect(() => {
    if (hasToken && !urlSessionId && !currentSessionId && !isGuest) {
      const lastSession = localStorage.getItem('lastCodemateSession');
      if (lastSession) {
        navigate(`/codemate/${lastSession}`, { replace: true });
        return;
      }
      (async () => {
        try {
          const session = await chatApi.startSession();
          navigate(`/codemate/${session.session_id}`, { replace: true });
        } catch (error) {
          console.error('[CHAT] Failed to create session after token:', error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  // Start new session
  const startNewSession = useCallback(async () => {
    setPathContext(null); // a fresh session is no longer a path step
    if (isGuest) {
      const id = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      setCurrentSessionId(id);
      setMessages([]);
    } else {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('auth_token');
      if (!token) return;
      try {
        localStorage.removeItem('lastCodemateSession');
        const session = await chatApi.startSession();
        setMessages([]);
        navigate(`/codemate/${session.session_id}`, { replace: true });
      } catch (error) {
        console.error('Failed to start session:', error);
      }
    }
  }, [isGuest, navigate]);

  // (exportSessionAsPDF removed - header Export button removed; per-message Export PDF via handleConvertToPdf stays)

  // Detect if response requires workspace (code execution or dashboard)
  // DEPRECATED: No longer auto-routing to workspace page. Users click buttons to open modal overlay.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const shouldRouteToWorkspace = (content: string, userMessage: string, hasCodeBlocks: boolean, isDashboard: boolean = false, backendIntent: string = ''): boolean => {
    // PRIORITY 1: Trust backend intent classification from LLM
    if (backendIntent) {
      const workspaceIntents = ['code', 'dual_code', 'visualization', 'execution'];
      const shouldRoute = workspaceIntents.includes(backendIntent.toLowerCase()) && hasCodeBlocks;

      if (shouldRoute) {
        console.log(`[CHAT ONLY] ✅ Routing to workspace - Backend intent: ${backendIntent}`);
        return true;
      } else {
        // If backend has classified intent as general/research, ALWAYS stay in chat regardless of code blocks
        console.log(`[CHAT ONLY] ❌ NOT routing - Backend intent: ${backendIntent} (staying in chat)`);
        return false;
      }
    }

    // PRIORITY 2: Trust backend's is_dashboard flag (from LLM classification)
    if (isDashboard && hasCodeBlocks) {
      console.log('[CHAT ONLY] Routing to workspace - Backend classified as dashboard');
      return true;
    }

    const contentLower = content.toLowerCase();
    const userLower = userMessage.toLowerCase();

    // Only route if there's actual executable code or dashboard visualization

    // 1. Check for explicit execution requests from user
    const executionKeywords = ['execute', 'run', 'test'];
    const hasExecutionIntent = executionKeywords.some(keyword => userLower.includes(keyword));

    // 2. Check for code generation requests (write, create program)
    const codeGenKeywords = ['write', 'create', 'generate', 'build', 'make'];
    const programKeywords = ['program', 'script', 'code', 'function'];
    const hasCodeGenIntent = codeGenKeywords.some(keyword => userLower.includes(keyword)) &&
                             programKeywords.some(keyword => userLower.includes(keyword));

    // 3. Check for dashboard/chart requests from user (fallback if backend didn't set flag)
    const dashboardKeywords = [
      'create a chart', 'create a dashboard', 'show me a chart',
      'bar chart', 'line chart', 'pie chart', 'scatter plot',
      'visualization', 'visualize', 'plot'
    ];
    const hasDashboardIntent = dashboardKeywords.some(keyword => userLower.includes(keyword));

    // 4. Check if backend returned HTML dashboard content (actual output)
    const hasHTMLDashboard = contentLower.includes('<html>') ||
                             contentLower.includes('plotly') ||
                             (contentLower.includes('dashboard') && contentLower.includes('generated'));

    // 5. Check if code blocks were extracted by backend (indicating executable code)
    const hasExecutableCode = hasCodeBlocks && (hasExecutionIntent || hasDashboardIntent || hasCodeGenIntent);

    // Route to workspace for:
    // - Code generation with code blocks (write program, create script, etc.)
    // - Explicit execution requests with code
    // - Dashboard/visualization requests with code
    // - HTML dashboard content
    return hasExecutableCode || hasHTMLDashboard ||
           (hasExecutionIntent && content.includes('```')) ||
           (hasDashboardIntent && content.includes('```'));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...files]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim() || loading || !currentSessionId) return;

    const messageText = input.trim();

    // Concierge: if there's a pending routing suggestion and the student is
    // saying yes, dispatch to the destination surface instead of sending
    // this through the chat pipeline.
    if (pendingSuggestion && isYesResponse(messageText)) {
      const modifier = extractYesModifier(messageText);
      const prefill = buildPrefillPrompt(pendingSuggestion.originalPrompt, modifier);
      console.log('[CONCIERGE] YES detected, dispatching to', pendingSuggestion.surface, 'with prefill:', prefill);
      setPendingSuggestion(null);
      setInput('');
      if (pendingSuggestion.surface === 'bookmate') {
        // CodebookPage reads ?prompt= for prefill and ?autosubmit=1 to start
        // generation immediately — student already said yes, no need to click again.
        navigate(`/bookmate?prompt=${encodeURIComponent(prefill)}&autosubmit=1`);
      } else if (pendingSuggestion.surface === 'workflow') {
        navigate(`/workflow?prompt=${encodeURIComponent(prefill)}&autosubmit=1`);
      }
      return;
    }

    // Any other message after a pending suggestion expires it silently.
    if (pendingSuggestion) {
      console.log('[CONCIERGE] Non-yes reply, clearing pending suggestion');
      setPendingSuggestion(null);
    }

    // Process attachments if any
    let attachments: Attachment[] | undefined = undefined;
    if (selectedFiles.length > 0) {
      attachments = await chatApi.processFiles(selectedFiles);
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
      attachments: attachments,
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSelectedFiles([]);
    setLoading(true);
    setIsStreamingActive(true); // Start streaming - disable auto-scroll

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = '40px';
    }

    const systemMessageId = (Date.now() + 1).toString();
    const systemMessage: Message = {
      id: systemMessageId,
      type: 'system',
      content: '',
      timestamp: new Date(),
    };

    // Don't add empty message - just show loading indicator
    // setMessages(prev => [...prev, systemMessage]);

    // Initialize thinking steps
    setThinkingSteps([]);

    let streamedContent = '';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let hasCodeBlocks = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let receivedCodeBlocks: any[] = [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let isDashboardRequest = false;
    let backendIntent = '';
    let messageAdded = false;

    // Path attribution (paths step 3): while this chat is a path step, tag
    // messages so the backend's learning_event marks the node completed.
    const activePathCtx = useChatStore.getState().pathContext;

    // Acquisition attribution (blog deep links): one-shot — consume the ref so
    // only the first message of the visit carries it.
    const deepLinkSource = deepLinkSourceRef.current;
    deepLinkSourceRef.current = null;

    try {
      await chatApi.sendMessageStream(
        currentSessionId,
        {
          message: messageText,
          attachments,
          ...(activePathCtx
            ? { path_id: activePathCtx.pathId, node_id: activePathCtx.nodeId }
            : {}),
          ...(deepLinkSource
            ? { source: deepLinkSource.source, source_ref: deepLinkSource.ref }
            : {}),
        },
        (chunk: string) => {
          streamedContent += chunk;

          // Add message on first chunk
          if (!messageAdded) {
            setMessages(prev => [...prev, { ...systemMessage, content: streamedContent, intent: backendIntent }]);
            messageAdded = true;
            setLoading(false); // Hide "thinking" indicator once streaming starts
          } else {
            // Update existing message
            setMessages(prev => prev.map(msg =>
              msg.id === systemMessageId
                ? { ...msg, content: streamedContent, intent: backendIntent }
                : msg
            ));
          }
        },
        (codeBlocks: any[], isDashboard?: boolean, intent?: string, files?: any[], isDualCode?: boolean) => {
          console.log('[CHAT ONLY] Received code blocks:', codeBlocks);
          console.log('[CHAT ONLY] Is dashboard:', isDashboard);
          console.log('[CHAT ONLY] Is dual code:', isDualCode);
          console.log('[CHAT ONLY] Backend intent:', intent);
          console.log('[CHAT ONLY] Generated files:', files);

          // 🚨 Store generated files in message
          if (files && files.length > 0) {
            console.log('[CHAT ONLY] Storing generated files in message:', files);
            setMessages(prev => prev.map(msg =>
              msg.id === systemMessageId
                ? { ...msg, generatedFiles: files }
                : msg
            ));
          }

          // Store dual code flag on message if detected
          if (isDualCode) {
            setMessages(prev => prev.map(msg =>
              msg.id === systemMessageId
                ? { ...msg, is_dual_code: true }
                : msg
            ));
          }

          if (codeBlocks && codeBlocks.length > 0) {
            hasCodeBlocks = true;
            receivedCodeBlocks = codeBlocks;
            isDashboardRequest = isDashboard || false;
            backendIntent = intent || '';
            console.log('[CHAT ONLY] Will check if redirect to workspace needed');

            // 🚨 CRITICAL: Append HTML code blocks to message content so they render
            // The backend sends HTML dashboards in the codeBlocks array, not in the text content
            // We need to append them as ```html blocks so renderMessageContent can display them
            const htmlBlocks = codeBlocks.filter(block => block.language?.toLowerCase() === 'html');
            if (htmlBlocks.length > 0) {
              console.log('[CHAT ONLY] Found HTML code blocks, appending to message:', htmlBlocks.length);
              let htmlContent = '';
              htmlBlocks.forEach((block: any) => {
                htmlContent += `\n\n\`\`\`html\n${block.code}\n\`\`\`\n`;
              });

              // Append HTML blocks to the streamed content
              streamedContent += htmlContent;

              // Update the message with HTML blocks included + store raw HTML for DashboardPreview
              setMessages(prev => prev.map(msg =>
                msg.id === systemMessageId
                  ? { ...msg, content: streamedContent, intent: intent || backendIntent, is_dashboard: isDashboard, htmlDashboard: htmlBlocks[0].code }
                  : msg
              ));
              console.log('[CHAT ONLY] HTML blocks appended to message content + htmlDashboard stored + is_dashboard flag:', isDashboard);
            }
          }

          // CRITICAL: Update message with intent and is_dashboard flag once received
          if (intent) {
            backendIntent = intent;
            setMessages(prev => prev.map(msg =>
              msg.id === systemMessageId
                ? { ...msg, intent: intent, is_dashboard: isDashboard }
                : msg
            ));
            console.log('[CHAT ONLY] Updated message with intent:', intent, 'is_dashboard:', isDashboard);
          }
        },
        (message: string, step: string, progress: number) => {
          // Status callback - update status notification and thinking steps
          console.log('[CHAT ONLY] Status update:', message, 'Step:', step, 'Progress:', progress);
          setStatusMessage(message);
          setStatusProgress(progress);

          // Update execution stages for code execution flow
          if (step === 'preparation' || step === 'executing') {
            setExecutionStages(prev => {
              // Initialize stages if empty
              if (prev.length === 0) {
                return [
                  { id: 'analyzing', label: 'Analyzing your request', status: 'completed' },
                  { id: 'generating', label: 'Generating code', status: 'completed' },
                  { id: 'preparing', label: 'Preparing execution environment', status: step === 'preparation' ? 'in_progress' : 'completed' },
                  { id: 'executing', label: 'Executing code', status: step === 'executing' ? 'in_progress' : 'pending' },
                ];
              }

              // Update existing stages
              return prev.map(stage => {
                if (stage.id === 'preparing' && step === 'preparation') {
                  return { ...stage, status: 'in_progress' };
                }
                if (stage.id === 'executing' && step === 'executing') {
                  return { ...stage, status: 'in_progress' };
                }
                if (stage.id === 'preparing' && step === 'executing') {
                  return { ...stage, status: 'completed' };
                }
                return stage;
              });
            });
          }

          // Create or update thinking step
          if (step && message) {
            console.log('[THINKING STEPS] Adding/updating step:', step, message);
            setThinkingSteps(prev => {
              const existingStepIndex = prev.findIndex(s => s.id === step);

              if (existingStepIndex !== -1) {
                // Update existing step
                const updated = [...prev];
                updated[existingStepIndex] = {
                  ...updated[existingStepIndex],
                  title: message,
                  progress: progress,
                  status: progress >= 100 ? 'completed' : 'in_progress',
                };
                console.log('[THINKING STEPS] Updated step. Total steps:', updated.length);
                return updated;
              } else {
                // Add new step
                const newSteps = [...prev, {
                  id: step,
                  title: message,
                  status: 'in_progress' as const,
                  timestamp: new Date(),
                  progress: progress,
                  isExpandable: false,
                }];
                console.log('[THINKING STEPS] Added new step. Total steps:', newSteps.length);
                return newSteps;
              }
            });
          }
        }
      );

      // NOTE: We no longer auto-navigate to workspace page.
      // Users manually click "Execute Code" or "View Dashboard" buttons to open the workspace modal overlay.
      console.log('[CHAT ONLY] Message received. User can click Execute Code/View Dashboard buttons to open workspace.');
      trackChatMessageSent(backendIntent);

      // Concierge: offer to dispatch to the right surface as a follow-up.
      // Bookmate gets priority — "teach me how to scrape" should route to
      // Bookmate (learning), not Workflow (automation), so Bookmate trigger
      // is checked first and Workflow only fires when Bookmate didn't match.
      //
      // Suppressed entirely while this chat is a Learning Path step: path
      // prompts are learning-flavored so they'd trip the Bookmate trigger on
      // nearly every step, and the path — not the concierge — owns "what
      // next" here. (Fresh store read: pathContext may have been set/cleared
      // since this handler's closure was created.)
      const onPathStep = useChatStore.getState().pathContext !== null;
      if (!onPathStep && detectBookmateTrigger(messageText)) {
        console.log('[CONCIERGE] Bookmate trigger matched for:', messageText);
        const conciergeMessage: Message = {
          id: `concierge_${Date.now()}`,
          type: 'system',
          content: '💡 Want me to build this as a full tutorial? Takes about a minute. Type "yes" to do it.',
          timestamp: new Date(),
          intent: 'concierge_suggestion',
        };
        setMessages(prev => [...prev, conciergeMessage]);
        setPendingSuggestion({
          surface: 'bookmate',
          originalPrompt: messageText,
          createdAt: Date.now(),
        });
      } else if (!onPathStep && detectWorkflowTrigger(messageText)) {
        console.log('[CONCIERGE] Workflow trigger matched for:', messageText);
        const conciergeMessage: Message = {
          id: `concierge_${Date.now()}`,
          type: 'system',
          content: '🔄 Want me to set this up as an automation? Takes 2-5 min and gives you a downloadable file. Type "yes" to do it.',
          timestamp: new Date(),
          intent: 'concierge_suggestion',
        };
        setMessages(prev => [...prev, conciergeMessage]);
        setPendingSuggestion({
          surface: 'workflow',
          originalPrompt: messageText,
          createdAt: Date.now(),
        });
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      const errorMsg = error?.message || '';
      const is402 = errorMsg.includes('Insufficient credits') || errorMsg.includes('PAYMENT_REQUIRED') || errorMsg.includes('trial has ended') || errorMsg.includes('Upgrade');
      // v3.19.7.1: surface the backend's actual error message (e.g. "Your free trial has
      // ended. Upgrade to Pro or Ultimate to continue.") rather than hardcoded v3.18 copy.
      const errorContent = is402
        ? (errorMsg.replace(/^PAYMENT_REQUIRED:?\s*/i, '') || 'Your free trial has ended. Choose a plan to continue.')
        : 'Sorry, there was an error processing your request.';
      if (messageAdded) {
        setMessages(prev => prev.map(msg =>
          msg.id === systemMessageId
            ? { ...msg, content: errorContent }
            : msg
        ));
      } else {
        // Message was never added (error before first chunk) — add it now
        setMessages(prev => [...prev, { ...systemMessage, content: errorContent }]);
      }
      // Redirect to paywall for credit/limit errors (v3.19.7.1: /pay, not /settings)
      if (is402) {
        setTimeout(() => navigate('/pay?reason=trial_expired'), 2500);
      }
    } finally {
      setLoading(false);
      setIsStreamingActive(false); // End streaming - enable auto-scroll
      // Clear status message when done
      setStatusMessage('');
      setStatusProgress(0);
      // Mark all thinking steps as completed (but keep them visible!)
      setThinkingSteps(prev => prev.map(step => ({
        ...step,
        status: step.status === 'in_progress' ? 'completed' : step.status
      })));
      // Mark all execution stages as completed
      setExecutionStages(prev => prev.map(stage => ({
        ...stage,
        status: 'completed'
      })));
      // DON'T clear thinking steps - keep them visible like Manus does!
      // They will be cleared when the user sends a new message (line 490)
      // Only clear execution stages after a short delay
      setTimeout(() => {
        setExecutionStages([]);
      }, 2000);
      // Session messages are now URL-based - no sessionStorage save needed
    }
  };

  const handleConvertToPdf = async (messageContent: string, messageId: string) => {
    setConvertingToPdf(messageId);
    try {
      // Clean the content - remove conversational parts and keep only the essay/report
      let cleanedContent = messageContent;

      // Remove all text before the first markdown heading (# or ##)
      const firstHeadingIndex = cleanedContent.search(/^#+\s+/m);
      if (firstHeadingIndex !== -1) {
        cleanedContent = cleanedContent.substring(firstHeadingIndex);
      }

      // Extract the main title from the first heading (# or ## or ### Title)
      const titleMatch = cleanedContent.match(/^#{1,3}\s+(.+?)$/m);
      const documentTitle = titleMatch ? titleMatch[1] : 'Research Document';

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/convert-to-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          markdown: cleanedContent.trim(),
          title: documentTitle,
          filename: `${documentTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${new Date().toISOString().split('T')[0]}`
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to convert to PDF: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.pdf_data) {
        // Create download link
        const linkSource = `data:application/pdf;base64,${result.pdf_data}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = result.pdf_filename || 'document.pdf';
        downloadLink.click();

        console.log('PDF generated and downloaded successfully!');
      } else {
        throw new Error(result.error || 'PDF generation failed');
      }
    } catch (err: any) {
      console.error('Failed to convert to PDF:', err);
      alert(`Failed to generate PDF: ${err.message}`);
    } finally {
      setConvertingToPdf(null);
    }
  };

  const renderMessageContent = (content: string) => {

    // ENHANCED: Support streaming code blocks (incomplete blocks without closing ```)
    // First, try to match complete code blocks OR incomplete streaming blocks
    const completeBlockPattern = /(```[\s\S]*?```)/;
    const parts = content.split(completeBlockPattern);

    return parts.map((part, index) => {
      // Handle COMPLETE code blocks (has both opening and closing ```)
      if (part.startsWith('```') && part.endsWith('```')) {
        // Extract language and code from the code block
        const firstLineEnd = part.indexOf('\n');
        const language = part.substring(3, firstLineEnd).trim();
        const code = part.substring(firstLineEnd + 1, part.length - 3).trim();

        // 🎨 SPECIAL HANDLING FOR HTML PRESENTATIONS (Nano Banana2 slides)
        // Check if this is an HTML presentation (contains slide navigation, <script>, etc.)
        const isHTMLPresentation = language?.toLowerCase() === 'html' && (
          code.includes('class="slide"') ||
          code.includes('currentSlide') ||
          code.includes('nextSlide') ||
          code.includes('previousSlide') ||
          code.includes('<script>') // Most presentations have JavaScript
        );

        if (isHTMLPresentation) {
          return (
            <div key={index} className="my-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg shadow-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-b border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🎨</span>
                  <div>
                    <h4 className="text-sm font-bold text-blue-700 dark:text-blue-300">Presentation Ready!</h4>
                    <p className="text-xs text-blue-600 dark:text-blue-400">Interactive slide deck generated with AI images</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      const blockId = `html-${index}`;
                      try {
                        navigator.clipboard.writeText(code).catch(() => {
                          const ta = document.createElement('textarea');
                          ta.value = code;
                          ta.style.position = 'fixed';
                          ta.style.left = '-9999px';
                          document.body.appendChild(ta);
                          ta.focus();
                          ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                        });
                      } catch {
                        const ta = document.createElement('textarea');
                        ta.value = code;
                        ta.style.position = 'fixed';
                        ta.style.left = '-9999px';
                        document.body.appendChild(ta);
                        ta.focus();
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                      setCopiedBlockId(blockId);
                      setTimeout(() => setCopiedBlockId(null), 2000);
                    }}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg transition-all shadow-sm hover:scale-105"
                    title="Copy HTML code"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>{copiedBlockId === `html-${index}` ? '✓ Copied!' : 'Copy HTML'}</span>
                  </button>
                  <button
                    onClick={() => {
                      // Open HTML in a new window
                      const blob = new Blob([code], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      const newWindow = window.open(url, '_blank');
                      if (newWindow) {
                        newWindow.document.title = 'Presentation - Cubemate';
                        // Clean up the object URL after the window loads
                        newWindow.addEventListener('load', () => {
                          setTimeout(() => URL.revokeObjectURL(url), 1000);
                        });
                      }
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm font-bold rounded-lg transition-all shadow-md hover:scale-105 hover:shadow-lg"
                    title="Open presentation in new window"
                  >
                    <span className="text-lg">🎬</span>
                    <span>View Slides</span>
                  </button>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <h5 className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">✨ Features:</h5>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1.5">
                    <li className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>AI-generated images embedded in slides</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>Keyboard navigation (Arrow keys, Space, Home, End)</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>Touch/swipe support for mobile devices</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>Progress bar and slide counter</span>
                    </li>
                  </ul>
                </div>
                <p className="text-xs text-center text-gray-500 dark:text-gray-500">
                  Click <strong className="text-blue-600 dark:text-blue-400">"View Slides"</strong> to open the presentation in a new window
                </p>
              </div>
            </div>
          );
        }

        // Regular code block (non-presentation)
        return (
          <div key={index} className="my-4 bg-card border border-border rounded-lg shadow-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-mono font-semibold uppercase rounded border border-primary/20">
                {language || 'code'}
              </span>
              <button
                onClick={() => {
                  const blockId = `code-${index}`;
                  try {
                    navigator.clipboard.writeText(code).catch(() => {
                      const ta = document.createElement('textarea');
                      ta.value = code;
                      ta.style.position = 'fixed';
                      ta.style.left = '-9999px';
                      document.body.appendChild(ta);
                      ta.focus();
                      ta.select();
                      document.execCommand('copy');
                      document.body.removeChild(ta);
                    });
                  } catch {
                    const ta = document.createElement('textarea');
                    ta.value = code;
                    ta.style.position = 'fixed';
                    ta.style.left = '-9999px';
                    document.body.appendChild(ta);
                    ta.focus();
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                  }
                  setCopiedBlockId(blockId);
                  setTimeout(() => setCopiedBlockId(null), 2000);
                }}
                className="flex items-center space-x-1 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground text-xs font-medium rounded transition-colors"
                title="Copy code"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>{copiedBlockId === `code-${index}` ? '✓ Copied!' : 'Copy'}</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <pre className="p-4 text-sm font-mono text-foreground bg-gradient-to-br from-background to-muted/20 w-full">
                <code className="block">{code}</code>
              </pre>
            </div>
          </div>
        );
      }
      // Handle INCOMPLETE/STREAMING code blocks (has opening ``` but no closing ```)
      else if (part.startsWith('```') && !part.endsWith('```')) {
        const firstLineEnd = part.indexOf('\n');
        if (firstLineEnd > 0) {
          const language = part.substring(3, firstLineEnd).trim();
          const code = part.substring(firstLineEnd + 1).trim();

          return (
            <div key={index} className="my-4 bg-card border border-border rounded-lg shadow-md w-full overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-mono font-semibold uppercase rounded border border-primary/20">
                  {language || 'code'}
                </span>
                <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span>Streaming...</span>
                </span>
              </div>
              <div className="overflow-x-auto">
                <pre className="p-4 text-sm font-mono text-foreground bg-gradient-to-br from-background to-muted/20 w-full">
                  <code className="block">{code}</code>
                </pre>
              </div>
            </div>
          );
        }
      }
      // Regular text content
      {
        // Check if content has tables (before cleaning)
        const lines = part.split('\n');
        let inTable = false;
        let tableRows: string[] = [];
        let formattedContent: string[] = [];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();

          // Detect table rows (contains | and multiple columns)
          if (line.includes('|') && line.split('|').length > 2) {
            if (!inTable) {
              inTable = true;
              tableRows = [];
            }
            tableRows.push(line);
          } else {
            // If we were in a table, render it
            if (inTable && tableRows.length > 0) {
              formattedContent.push(renderTable(tableRows));
              tableRows = [];
              inTable = false;
            }

            // Process regular line - clean markdown first
            if (line) {
              formattedContent.push(formatLine(line));
            } else {
              formattedContent.push('<div class="h-3"></div>');
            }
          }
        }

        // Handle remaining table
        if (inTable && tableRows.length > 0) {
          formattedContent.push(renderTable(tableRows));
        }

        return (
          <div
            key={index}
            className="text-[15px] leading-relaxed space-y-1"
            dangerouslySetInnerHTML={{ __html: formattedContent.join('') }}
          />
        );
      }
    });
  };

  const formatLine = (line: string): string => {
    // Headers MUST be checked FIRST before any other formatting
    if (line.startsWith('### ')) {
      const text = line.substring(4).trim();
      return '<h3 class="text-base font-semibold mt-4 mb-2 text-primary">' + cleanInlineMarkdown(text) + '</h3>';
    } else if (line.startsWith('## ')) {
      const text = line.substring(3).trim();
      return '<h2 class="text-lg font-bold mt-5 mb-2 text-primary">' + cleanInlineMarkdown(text) + '</h2>';
    } else if (line.startsWith('# ') && !line.startsWith('##')) {
      const text = line.substring(2).trim();
      return '<h1 class="text-xl font-bold mt-5 mb-3 text-primary">' + cleanInlineMarkdown(text) + '</h1>';
    }

    // Horizontal rule
    if (line.startsWith('---') || line.startsWith('***')) {
      return '<hr class="my-4 border-border" />';
    }

    // GENERIC LIST DETECTION: Check if line already contains list markers
    // This handles: "• text", "• • text", "• 1. text", "1. text", "- text", etc.
    const trimmedLine = line.trim();

    // Check if content already has visual list markers (bullet, number, dash, asterisk)
    const hasListMarker = /^[•\-*\d+.]\s/.test(trimmedLine);

    if (hasListMarker) {
      // Content already formatted - just render it as-is without adding extra markers
      return '<li class="ml-4 mb-1.5">' + cleanInlineMarkdown(trimmedLine) + '</li>';
    }

    // Legacy markdown list detection for clean content without markers
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const listContent = cleanInlineMarkdown(line.substring(2).trim());
      return '<li class="ml-4 mb-1.5">• ' + listContent + '</li>';
    }

    // Empty line - create paragraph break
    if (line.trim() === '') {
      return '<div class="h-3"></div>';
    }

    // Regular text with inline formatting
    // Check if next line is also text (not a header/list) to add proper spacing
    return '<span class="block mb-2">' + cleanInlineMarkdown(line) + '</span>';
  };

  const cleanInlineMarkdown = (text: string): string => {
    // AGGRESSIVE CLEANING - remove all markdown first, then format

    // 0. FIRST: Escape HTML tags so they display as text (critical for dashboard HTML streaming)
    // This prevents browser from rendering incomplete HTML during streaming
    text = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // 1. Remove header markdown (##, ###) - both at start and mid-line
    text = text.replace(/###\s*/g, '');
    text = text.replace(/##\s*/g, '');
    text = text.replace(/#\s+/g, '');

    // 2. Handle bold text **text** - convert to HTML
    text = text.replace(/\*\*([^*]+?)\*\*/g, '<strong class="font-semibold">$1</strong>');

    // 3. Handle italic/emphasis *text* or _text_
    text = text.replace(/\*([^*]+?)\*/g, '<em class="italic">$1</em>');
    text = text.replace(/_([^_]+?)_/g, '<em class="italic">$1</em>');

    // 4. Inline code `code`
    text = text.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-muted text-foreground rounded text-xs font-mono">$1</code>');

    // 5. Convert URLs to clickable links
    // Match http://, https://, and www. URLs
    text = text.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline">$1</a>'
    );

    // 6. Remove table pipes
    text = text.replace(/\|/g, ' ');

    // 7. Remove horizontal rules
    text = text.replace(/---+/g, '');
    text = text.replace(/___+/g, '');
    text = text.replace(/\*\*\*+/g, '');

    // 8. Clean up extra spaces
    text = text.replace(/\s{2,}/g, ' ');
    text = text.trim();

    return text;
  };

  const renderTable = (rows: string[]): string => {
    if (rows.length < 2) return rows.join('<br />');

    let html = '<table class="my-4 min-w-full border-collapse border border-border rounded-lg overflow-hidden"><tbody>';

    rows.forEach((row, idx) => {
      // Skip separator rows (---|---|---)
      if (row.includes('---')) return;

      const cells = row.split('|').filter(cell => cell.trim());
      const isHeader = idx === 0;

      html += '<tr class="' + (isHeader ? 'bg-muted font-semibold' : 'even:bg-muted/30') + '">';
      cells.forEach(cell => {
        const tag = isHeader ? 'th' : 'td';
        html += `<${tag} class="border border-border px-4 py-2 text-left">${cell.trim()}</${tag}>`;
      });
      html += '</tr>';
    });

    html += '</tbody></table>';
    return html;
  };


  // NOTE: Button visibility is 100% AI-DRIVEN based on backend intent classification.
  // The AI (Azure OpenAI Gatekeeper) analyzes the user request and sets the intent.
  // Frontend simply displays buttons based on the AI's decision.
  // This ensures consistency and leverages the AI's understanding of user needs.

  // Handler to open workspace modal with code for execution
  const handleExecuteCode = (messageContent: string, messageId: string) => {
    console.log('[CHAT] Opening workspace modal for code execution');

    // Extract code blocks from message
    // More flexible regex: allow optional whitespace after language identifier
    const codeBlockPattern = /```(\w+)?\s*\n(.*?)```/gs;
    const matches = Array.from(messageContent.matchAll(codeBlockPattern));

    const codeBlocks = matches.map(match => ({
      language: match[1] || 'python',
      code: match[2]
    }));

    console.log('[CHAT] Extracted code blocks for execution:', codeBlocks.length);

    // Open workspace modal with code blocks
    setWorkspaceContent({
      type: 'code',
      codeBlocks,
      messageId
    });
    setWorkspaceModalOpen(true);
  };

  // Handler to open workspace modal with dashboard view
  const handleViewDashboard = (messageContent: string, messageId: string, htmlDashboard?: string) => {
    console.log('[CHAT] Opening workspace modal for dashboard view');
    console.log('[CHAT] Message content length:', messageContent.length);
    console.log('[CHAT] Pre-rendered HTML available:', !!htmlDashboard);

    // Extract all code blocks from message (Python or HTML)
    // More flexible regex: allow optional whitespace after language identifier
    const codeBlockPattern = /```(\w+)?\s*\n(.*?)```/gs;
    const matches = Array.from(messageContent.matchAll(codeBlockPattern));

    const codeBlocks = matches.map(match => ({
      language: match[1] || 'python',
      code: match[2]
    }));

    console.log('[CHAT] Extracted code blocks:', codeBlocks.length);
    if (codeBlocks.length > 0) {
      codeBlocks.forEach((block, idx) => {
        console.log(`[CHAT] Block ${idx + 1}: ${block.language} (${block.code.length} chars)`);
      });
    }

    // Open workspace modal with dashboard (pass pre-rendered HTML if available)
    setWorkspaceContent({
      type: 'dashboard',
      codeBlocks,
      messageId,
      htmlDashboard
    });
    setWorkspaceModalOpen(true);
  };

  // Handler to open workspace modal with dual code (side-by-side comparison)
  const handleCompareDualCode = (messageContent: string, messageId: string) => {
    console.log('[CHAT] Opening workspace modal for dual code comparison');

    // Extract code blocks from message
    const codeBlockPattern = /```(\w+)?\s*\n(.*?)```/gs;
    const matches = Array.from(messageContent.matchAll(codeBlockPattern));

    const codeBlocks = matches.map(match => ({
      language: match[1] || 'text',
      code: match[2]?.trim() || ''
    }));

    console.log('[CHAT] Extracted dual code blocks:', codeBlocks.length);
    if (codeBlocks.length >= 2) {
      console.log(`[CHAT] Language 1: ${codeBlocks[0].language}, Language 2: ${codeBlocks[1].language}`);
    }

    // Extract comparison notes (text after the second code block, starting with **Key Differences:**)
    const comparisonMatch = messageContent.match(/\*\*Key Differences:\*\*([\s\S]*?)(?:```|$)/);
    const comparisonNotes = comparisonMatch ? comparisonMatch[1].trim() : undefined;

    setWorkspaceContent({
      type: 'dual_code',
      codeBlocks,
      messageId,
      comparisonNotes
    });
    setWorkspaceModalOpen(true);
  };

  // Handler to open workspace modal with slide view
  const handleViewSlides = (messageContent: string, messageId: string) => {
    console.log('[CHAT] Opening workspace modal for slide view');
    console.log('[CHAT] Message content length:', messageContent.length);

    // Extract PDF data from message
    const pdfPattern = /(data:application\/pdf;base64,[A-Za-z0-9+/=]+)/;
    const pdfMatch = messageContent.match(pdfPattern);

    if (!pdfMatch) {
      console.error('[CHAT] No PDF data found in message');
      return;
    }

    const pdfData = pdfMatch[1];
    console.log('[CHAT] Extracted PDF data:', pdfData.substring(0, 50) + '...');

    // Open workspace modal with slides
    setWorkspaceContent({
      type: 'slides',
      pdfData,
      messageId
    });
    setWorkspaceModalOpen(true);
  };

  // Handler for documentation prompt clicks
  const handleDocumentationPromptClick = (prompt: string) => {
    setInput(prompt);
    // Focus on textarea after prompt is set
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  // Feature-guided example prompts — teach users when to use each feature
  const [tabTooltip, setTabTooltip] = useState<string | null>(null);
  const featureExamples = [
    {
      label: 'Chat',
      description: 'Write and run code in any language',
      prompts: [
        'What is a JavaScript closure?',
        'Implement binary search in Python and let me run it',
      ],
      route: null,  // stays in chat
    },
    {
      label: 'Bookmate',
      description: 'Side-by-side language comparison tutorials',
      prompts: [
        'I know Python, teach me how error handling works in Java',
      ],
      route: '/bookmate',
    },
    {
      label: 'Workflow',
      description: 'Download datasets and generate charts',
      prompts: [
        'Download the Iris dataset and create a scatter plot colored by species',
      ],
      route: '/workflow',
    },
  ];

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between px-3 py-3 md:px-6 md:py-4">
          <div className="flex items-center space-x-2 md:space-x-4 pl-10 md:pl-0">
            <img
              src="/cubemate-logo.png"
              alt="Cubemate"
              className="w-8 h-8 md:w-10 md:h-10 rounded-lg"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-base md:text-xl font-bold bg-gradient-to-r from-primary to-brand bg-clip-text text-transparent">
                  Cubemate
                </h1>
              </div>
              <p className="hidden md:block text-xs text-muted-foreground/80 font-normal italic">
                {tagline}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1.5 md:space-x-3">
            {(user as any)?.subscription_tier !== 'ultimate' && (
              <button
                onClick={() => navigate('/settings?tab=subscription')}
                className="hidden md:block px-4 py-2 text-sm bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white hover:shadow-lg hover:shadow-primary/30 rounded-lg transition-all duration-200 font-semibold"
                title="Upgrade to premium"
              >
                Upgrade +
              </button>
            )}
            <span className="hidden md:inline-flex"><DocumentationButton onClick={() => setDocumentationOpen(true)} /></span>
            <button
              onClick={startNewSession}
              className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white hover:shadow-lg hover:shadow-primary/30 rounded-lg transition-all duration-200 font-semibold flex items-center space-x-1.5"
              title="Start a new chat session"
            >
              <span>+ New</span>
            </button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Documentation Modal */}
      <DocumentationModal
        isOpen={documentationOpen}
        onClose={() => setDocumentationOpen(false)}
        onPromptClick={handleDocumentationPromptClick}
        currentRoute="chat"
      />

      {/* Onboarding/welcome modal — first login only */}
      {welcomeMode && (
        <OnboardingModal mode={welcomeMode} onClose={() => {
          setWelcomeMode(null);
          sessionStorage.setItem('welcome_dismissed', 'true');
        }} />
      )}

      {/* Pro Trial banner — basic users only, dismissible */}
      {showProTrial && !welcomeMode && (
        <ProTrialBanner onClose={() => {
          setShowProTrial(false);
          sessionStorage.setItem('pro_trial_dismissed', 'true');
          trackProBannerDismissed();
        }} />
      )}

      {/* Body: Sidebar + Main Content */}
      <div className="flex flex-1 overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-primary/10 via-transparent to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-brand/10 via-transparent to-transparent rounded-full blur-3xl animate-pulse-slower"></div>
        </div>

        {/* Sidebar */}
        <Sidebar />

        {/* Chat Container - Full width */}
        <div className="flex-1 flex flex-col overflow-hidden relative">

          {/* Learning Path banner — shown while this chat is a path step
              (opened via a path's Start → deep link). Chat has no natural
              "end" like a Bookmate tutorial, so the way back stays visible
              until dismissed or a new session starts. */}
          {pathContext && (
            <div className="flex-shrink-0 flex items-center justify-between gap-3 mx-3 md:mx-4 mt-3 px-4 py-2.5 rounded-xl border border-purple-300 dark:border-purple-700 bg-purple-50/80 dark:bg-purple-900/20 backdrop-blur-sm relative z-10 animate-fade-in">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-lg flex-shrink-0">🧭</span>
                <span className="text-sm text-foreground truncate">
                  You're working on a step from your learning path.
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate(`/paths/${pathContext.pathId}`)}
                  className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-semibold shadow hover:from-purple-600 hover:to-blue-600 transition-all whitespace-nowrap"
                >
                  ← Back to path
                </button>
                <button
                  onClick={() => setPathContext(null)}
                  className="w-6 h-6 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {messages.length === 0 ? (
            /* Empty State - Centered Layout */
            <div className="flex-1 flex flex-col items-center justify-center px-3 md:px-4 pb-8 md:pb-16">
              <div className="text-center space-y-3 md:space-y-4 max-w-2xl mb-6 md:mb-8 animate-fade-in">
                <h2 className="text-2xl md:text-4xl font-semibold text-gray-900 dark:text-white leading-tight">
                  What would you like to do?
                </h2>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                  Python, Java, C, C++, JavaScript, Rust, Go
                </p>
              </div>

              {/* Input Form - Centered */}
              <div className="w-full max-w-3xl relative z-10">
                <form onSubmit={handleSendMessage} className="relative">
                  {/* Attachment preview area */}
                  {selectedFiles.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2 animate-fade-in">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl text-sm shadow-md hover:shadow-lg transition-all">
                          <span className="text-lg">📎</span>
                          <span className="text-foreground font-medium">{file.name}</span>
                          <span className="text-muted-foreground text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="ml-2 text-muted-foreground hover:text-destructive transition-colors hover:scale-110 transform"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="relative w-full">
                    <div className="relative flex items-center bg-white dark:bg-card border border-border/30 rounded-full p-3 shadow-xl hover:shadow-2xl transition-all">
                      {/* Plus button - for attachments */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        accept=".txt,.py,.js,.json,.csv,.md,.html,.css,.java,.cpp,.c,.tsx,.ts"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading}
                        className="flex-shrink-0 w-10 h-10 text-muted-foreground hover:text-foreground rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 transition-all flex items-center justify-center hover:bg-muted/50"
                        title="Attach files"
                      >
                        <span className="text-2xl font-light">+</span>
                      </button>
                      <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                          setInput(e.target.value);
                          if (textareaRef.current) {
                            textareaRef.current.style.height = 'auto';
                            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (!loading && input.trim() && currentSessionId) {
                              handleSendMessage(e as any);
                            }
                          }
                        }}
                        placeholder={pendingSuggestion ? "Type 'yes' to build a full tutorial — or keep chatting" : "Try: 'What is a JavaScript closure?' or 'Write a merge sort in Python'"}
                        disabled={loading}
                        rows={1}
                        className="flex-1 px-4 py-3 bg-transparent text-foreground placeholder-muted-foreground/60 focus:outline-none resize-none min-h-[40px] max-h-[120px] overflow-y-auto text-[15px]"
                      />
                      <button
                        type="submit"
                        disabled={loading || !input.trim() || !currentSessionId}
                        className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white rounded-full hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                      >
                        {loading ? (
                          <span className="text-lg">⏳</span>
                        ) : (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
                {/* Feature-Guided Examples — compact flat layout */}
                <div className="mt-4 w-full animate-fade-in">
                  {featureExamples.map((feature, fIdx) => (
                    <div key={fIdx} className="flex items-start gap-2 py-1.5 relative">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 ${
                        feature.label === 'Chat' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                        feature.label === 'Bookmate' ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400' :
                        'bg-green-500/10 text-green-600 dark:text-green-400'
                      }`}>
                        {feature.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        {feature.prompts.map((prompt, pIdx) => (
                          <button
                            key={pIdx}
                            onClick={() => {
                              if (feature.route) {
                                // Navigate to the correct module with prompt pre-filled
                                const loginCount = parseInt(sessionStorage.getItem('login_count') || '0', 10);
                                if (loginCount < 5) {
                                  setTabTooltip(feature.label);
                                  setTimeout(() => setTabTooltip(null), 3000);
                                }
                                setTimeout(() => {
                                  navigate(feature.route + '?prompt=' + encodeURIComponent(prompt));
                                }, 300);
                              } else {
                                setInput(prompt);
                              }
                            }}
                            className="block text-sm leading-snug transition-colors text-gray-600 dark:text-gray-400 hover:text-primary cursor-pointer"
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Tab change tooltip — shown for new users (login < 5) */}
                  {tabTooltip && (
                    <div className="fixed left-16 top-1/3 z-50 animate-fade-in">
                      <div className={`px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg border ${
                        tabTooltip === 'Bookmate'
                          ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                          : 'bg-green-500/20 text-green-300 border-green-500/30'
                      }`}>
                        <span className="mr-1">{tabTooltip === 'Bookmate' ? '\u2190' : '\u2190'}</span>
                        Opening in <strong>{tabTooltip}</strong> tab
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-center text-muted-foreground mt-2">
                    Not sure? <a href="/guide" className="text-primary hover:underline">Help / How to Start</a>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* With Messages - Scrollable Layout */
            <div className="flex-1 w-full max-w-full md:max-w-[90%] mx-auto flex flex-col overflow-hidden">
              {/* Messages - Scrollable area */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto space-y-6 py-6 px-4 relative z-10">

            {(() => { const lastUserIdx = messages.reduce((acc, m, i) => m.type === 'user' ? i : acc, -1); return messages.map((message, index) => (
              <div
                key={message.id}
                ref={message.type === 'user' && index === lastUserIdx ? lastUserMessageRef : undefined}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}>
                <div className={`${message.type === 'user' ? 'max-w-2xl' : 'max-w-7xl w-full'} rounded-2xl p-5 backdrop-blur-sm ${
                  message.type === 'user'
                    ? 'bg-primary text-primary-foreground border border-primary/20'
                    : 'bg-card/80 border border-border/50'
                } transition-all duration-200`}>
                  <div className="flex items-start space-x-3">
                    {message.type === 'user' ? (
                      <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base bg-primary-foreground/20 border border-primary-foreground/30">
                        👨‍💻
                      </div>
                    ) : (
                      <img
                        src="/cubemate-logo.png"
                        alt="Cubemate"
                        className="flex-shrink-0 w-9 h-9 rounded-lg"
                      />
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      {/* Execution stages - shown ABOVE content for the last message being streamed */}
                      {message.type === 'system' &&
                       message.id === messages[messages.length - 1]?.id &&
                       executionStages.length > 0 && (
                        <div className="mb-4">
                          <ExecutionStages stages={executionStages} />
                        </div>
                      )}

                      {/* Thinking steps - shown ABOVE content for assistant messages (Manus-style) */}
                      {message.type === 'system' &&
                       message.id === messages[messages.length - 1]?.id &&
                       thinkingSteps.length > 0 && (
                        <div className="mb-4">
                          <ThinkingSteps steps={thinkingSteps} />
                        </div>
                      )}

                      <div className="w-full">
                        {message.intent === 'workflow' ? (
                          <WorkflowMessage
                            content={message.content}
                            generatedFiles={(message as any).generatedFiles}
                          />
                        ) : message.type === 'user' && isLongUserMsg(message.content) ? (
                          // Google-AI-style collapse: long prompts clamp to 3
                          // lines with a chevron to expand/collapse.
                          (() => {
                            const expanded = expandedUserMsgs.has(message.id);
                            return (
                              <div>
                                {expanded ? (
                                  renderMessageContent(message.content)
                                ) : (
                                  <div
                                    className="whitespace-pre-wrap break-words"
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {message.content}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleUserMsg(message.id)}
                                  className="mt-2 ml-auto flex items-center gap-1 text-xs font-medium opacity-75 hover:opacity-100 transition-opacity"
                                  title={expanded ? 'Show less' : 'Show full message'}
                                >
                                  {expanded ? 'Show less' : 'Show more'}
                                  <svg
                                    viewBox="0 0 24 24"
                                    className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M6 9l6 6 6-6" />
                                  </svg>
                                </button>
                              </div>
                            );
                          })()
                        ) : (
                          renderMessageContent(message.content)
                        )}
                      </div>

                      {/* Action buttons - 100% AI-DRIVEN based on backend intent classification */}
                      {/* Positioned below message content on the left side */}
                      {message.type !== 'user' && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {/* Execute Code Button - Shows when code intent AND NOT a dashboard (dashboards have their own button) */}
                          {(() => {
                            const hasActualCode = /```/i.test(message.content);
                            // Don't show Execute Code if this is a dashboard (Python visualization or HTML)
                            if (message.is_dashboard) {
                              return null;
                            }
                            return ['code', 'code_execution', 'execution'].includes(message.intent || '') && hasActualCode && (
                              <button
                                onClick={() => handleExecuteCode(message.content, message.id)}
                                className="group relative px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 rounded-lg transition-all flex items-center space-x-1.5 font-medium hover:scale-105 overflow-hidden"
                                title="Execute this code in workspace"
                              >
                                <span className="absolute inset-0 rounded-lg border border-blue-500/40 group-hover:border-blue-500/70 transition-colors" />
                                <span className="absolute inset-0 rounded-lg" style={{
                                  background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.15), transparent)',
                                  backgroundSize: '200% 100%',
                                  animation: 'borderGlow 3s ease-in-out infinite',
                                }} />
                                <span className="relative">Execute Code</span>
                              </button>
                            );
                          })()}

                          {/* Compare & Run Button - Shows for dual_code intent (side-by-side language comparison) */}
                          {message.intent === 'dual_code' && /```/i.test(message.content) && (
                            <button
                              onClick={() => handleCompareDualCode(message.content, message.id)}
                              className="group relative px-3 py-1.5 text-xs text-purple-600 dark:text-purple-400 rounded-lg transition-all flex items-center space-x-1.5 font-medium hover:scale-105 overflow-hidden"
                              title="View side-by-side code comparison with execution"
                            >
                              {/* Animated border */}
                              <span className="absolute inset-0 rounded-lg border border-purple-500/40 group-hover:border-purple-500/70 transition-colors" />
                              <span className="absolute inset-0 rounded-lg animate-[borderGlow_3s_ease-in-out_infinite]" style={{
                                background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.15), transparent)',
                                backgroundSize: '200% 100%',
                                animation: 'borderGlow 3s ease-in-out infinite',
                              }} />
                              <span className="relative">{'</>'}</span>
                              <span className="relative">Compare & Run</span>
                            </button>
                          )}

                          {/* Download Files Button - Shows when workflow generated CSV/Excel/JSON files */}
                          {(message as any).generatedFiles && (message as any).generatedFiles.length > 0 && (
                            <button
                              onClick={() => {
                                const files = (message as any).generatedFiles;
                                console.log('[DOWNLOAD] Opening file:', files[0]);
                                if (files[0].url) {
                                  const apiUrl = process.env.REACT_APP_API_URL || '';
                                  const url = files[0].url.replace(/https?:\/\/localhost:\d+/, apiUrl);
                                  window.open(url, '_blank');
                                }
                              }}
                              className="group relative px-3 py-1.5 text-xs text-green-600 dark:text-green-400 rounded-lg transition-all flex items-center space-x-1.5 font-medium hover:scale-105 overflow-hidden"
                              title="Download generated file"
                            >
                              <span className="absolute inset-0 rounded-lg border border-green-500/40 group-hover:border-green-500/70 transition-colors" />
                              <span className="absolute inset-0 rounded-lg" style={{
                                background: 'linear-gradient(90deg, transparent, rgba(34,197,94,0.15), transparent)',
                                backgroundSize: '200% 100%',
                                animation: 'borderGlow 3s ease-in-out infinite',
                              }} />
                              <span className="relative">Download</span>
                            </button>
                          )}

                          {/* View Dashboard Button - Shows when backend sets is_dashboard flag */}
                          {(() => {
                            // Never show if there are generated files (CSV/Excel downloads)
                            if ((message as any).generatedFiles && (message as any).generatedFiles.length > 0) {
                              return null;
                            }

                            // Simple check: if backend says it's a dashboard, show the button
                            if (message.is_dashboard) {
                              const hasActualDashboard = /```|<html|<!DOCTYPE|<canvas|<svg|matplotlib|plotly|chart\.js/i.test(message.content);
                              if (hasActualDashboard) {
                                return (
                                  <button
                                    onClick={() => handleViewDashboard(message.content, message.id, (message as any).htmlDashboard)}
                                    className="group relative px-3 py-1.5 text-xs text-purple-600 dark:text-purple-400 rounded-lg transition-all flex items-center space-x-1.5 font-medium hover:scale-105 overflow-hidden"
                                    title="View dashboard in workspace"
                                  >
                                    <span className="absolute inset-0 rounded-lg border border-purple-500/40 group-hover:border-purple-500/70 transition-colors" />
                                    <span className="absolute inset-0 rounded-lg" style={{
                                      background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.15), transparent)',
                                      backgroundSize: '200% 100%',
                                      animation: 'borderGlow 3s ease-in-out infinite',
                                    }} />
                                    <span className="relative">View Dashboard</span>
                                  </button>
                                );
                              }
                            }

                            return null;
                          })()}

                          {/* View Slides Button - Shows when intent='slides' */}
                          {message.intent === 'slides' && /data:application\/pdf;base64/i.test(message.content) && (
                            <button
                              onClick={() => handleViewSlides(message.content, message.id)}
                              className="px-3 py-1.5 text-xs bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-lg transition-all flex items-center space-x-1.5 shadow-sm font-medium hover:scale-105"
                              title="View slides in workspace"
                            >
                              <span>📊</span>
                              <span>View Slides</span>
                            </button>
                          )}

                          {/* Export PDF Button - ONLY for research/document intent, NOT for dashboards/visualizations */}
                          {['research', 'document'].includes(message.intent || '') && message.content.length > 300 && (
                            <button
                              onClick={() => handleConvertToPdf(message.content, message.id)}
                              disabled={convertingToPdf === message.id}
                              className="px-3 py-1.5 text-xs bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 rounded-lg transition-all disabled:opacity-50 flex items-center space-x-1.5 shadow-sm font-medium hover:scale-105"
                              title="Export this response as PDF"
                            >
                              {convertingToPdf === message.id ? (
                                <>
                                  <span className="animate-spin">⏳</span>
                                  <span>Converting...</span>
                                </>
                              ) : (
                                <>
                                  <span>📄</span>
                                  <span>Export PDF</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )); })()}
            {/* Loading and thinking steps indicator */}
            {loading && (
              <div className="flex justify-start animate-fade-in-up">
                <div className="bg-card/80 border border-border/50 rounded-2xl p-5 backdrop-blur-sm max-w-3xl">
                  <div className="flex items-start space-x-3">
                    <img
                      src="/cubemate-logo.png"
                      alt="Cubemate"
                      className="flex-shrink-0 w-9 h-9 rounded-lg animate-spin"
                      style={{ animationDuration: '3s' }}
                    />
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center space-x-3">
                        {/* Show status message inline if available, otherwise default "thinking" */}
                        <span className="text-sm text-muted-foreground font-medium">
                          {statusMessage || 'Cubemate is thinking'}
                        </span>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gradient-to-r from-primary to-brand rounded-full animate-bounce shadow-sm" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-gradient-to-r from-primary to-brand rounded-full animate-bounce shadow-sm" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-gradient-to-r from-primary to-brand rounded-full animate-bounce shadow-sm" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                      {/* Thinking steps are now shown in the message itself, not here */}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Form - Sticky at bottom */}
          <div className="flex-shrink-0 px-2 py-3 md:p-4 relative z-10">
            <form onSubmit={handleSendMessage} className="relative">
              {/* Attachment preview area */}
              {selectedFiles.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2 animate-fade-in">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl text-sm shadow-md hover:shadow-lg transition-all">
                      <span className="text-lg">📎</span>
                      <span className="text-foreground font-medium">{file.name}</span>
                      <span className="text-muted-foreground text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-2 text-muted-foreground hover:text-destructive transition-colors hover:scale-110 transform"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="relative max-w-3xl mx-auto w-full">
                <div className="relative flex items-center bg-white dark:bg-card border border-border/30 rounded-full p-3 shadow-xl hover:shadow-2xl transition-all">
                  {/* Plus button - for attachments */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".txt,.py,.js,.json,.csv,.md,.html,.css,.java,.cpp,.c,.tsx,.ts"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    className="flex-shrink-0 w-10 h-10 text-muted-foreground hover:text-foreground rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 transition-all flex items-center justify-center hover:bg-muted/50"
                    title="Attach files"
                  >
                    <span className="text-2xl font-light">+</span>
                  </button>

                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      if (textareaRef.current) {
                        textareaRef.current.style.height = 'auto';
                        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!loading && input.trim()) {
                          handleSendMessage(e as any);
                        }
                      }
                    }}
                    placeholder={pendingSuggestion ? "Type 'yes' to build a full tutorial — or keep chatting" : "Try: 'What is a JavaScript closure?' or 'Write a merge sort in Python'"}
                    disabled={loading}
                    rows={1}
                    className="flex-1 px-4 py-3 bg-transparent text-foreground placeholder-muted-foreground/60 focus:outline-none resize-none min-h-[40px] max-h-[120px] overflow-y-auto text-[15px]"
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim() || !currentSessionId}
                    className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white rounded-full hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                  >
                    {loading ? (
                      <span className="text-lg">⏳</span>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
          </div>
        )}
        {/* End of Conditional (Empty vs With Messages) */}
        </div>
        {/* End of Chat Container div */}
      </div>
      {/* End of flex-1 main content div */}

      {/* Workspace Modal Overlay */}
      {workspaceModalOpen && workspaceContent && (
        <div className="fixed inset-0 z-50 bg-background">
          {/* Header with Close Button */}
          <div className="h-12 md:h-14 bg-card border-b border-border flex items-center justify-between px-3 md:px-6 shadow-lg">
            <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-7 h-7 md:w-8 md:h-8 rounded-lg flex-shrink-0" />
              <h3 className="text-sm md:text-lg font-bold text-foreground truncate">
                {workspaceContent.type === 'code'
                  ? 'Code Execution'
                  : workspaceContent.type === 'dual_code'
                    ? 'Compare & Run'
                    : workspaceContent.type === 'slides'
                      ? 'Slides'
                      : 'Dashboard'}
              </h3>
            </div>
            <button
              onClick={() => setWorkspaceModalOpen(false)}
              className="px-3 py-1.5 md:px-4 md:py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors font-medium text-foreground flex items-center space-x-1 md:space-x-2 shadow-sm text-sm flex-shrink-0"
            >
              <span>✕</span>
              <span className="hidden md:inline">Close</span>
            </button>
          </div>

          {/* Workspace Content */}
          <div className="h-[calc(100vh-3.5rem)] overflow-auto">
            {workspaceContent.type === 'code' ? (
              <CodeDisplay
                codeBlocks={workspaceContent.codeBlocks}
                currentMessageId={workspaceContent.messageId}
                sessionId={currentSessionId || undefined}
              />
            ) : workspaceContent.type === 'dual_code' ? (
              <div className="p-3 md:p-6 max-w-7xl mx-auto">
                {workspaceContent.codeBlocks && workspaceContent.codeBlocks.length >= 2 && (
                  <CodebookDualCodeBlock
                    language_primary={workspaceContent.codeBlocks[0].language}
                    code_primary={workspaceContent.codeBlocks[0].code}
                    language_secondary={workspaceContent.codeBlocks[1].language}
                    code_secondary={workspaceContent.codeBlocks[1].code}
                    comparison_notes={workspaceContent.comparisonNotes}
                  />
                )}
              </div>
            ) : workspaceContent.type === 'slides' ? (
              <div className="w-full h-full flex flex-col bg-background">
                {/* PDF Preview */}
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="w-full max-w-6xl h-full bg-card rounded-lg shadow-2xl overflow-hidden border-2 border-border">
                    <iframe
                      src={workspaceContent.pdfData}
                      className="w-full h-full"
                      title="Slide Presentation"
                    />
                  </div>
                </div>
                {/* Download Button */}
                <div className="flex-shrink-0 bg-card border-t border-border p-4 flex justify-center">
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = workspaceContent.pdfData;
                      link.download = `presentation-${Date.now()}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-lg transition-all shadow-lg hover:scale-105 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            ) : (
              <DashboardPreview
                codeBlocks={workspaceContent.codeBlocks}
                currentMessageId={workspaceContent.messageId}
                initialHtml={workspaceContent.htmlDashboard}
                sessionId={currentSessionId || undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatOnlyPage;
